// @greenlight-spec: 19-design-system.md
import * as React from 'react';

/**
 * Reports whether an async action is still running, so the control that started it
 * can disable and say so.
 *
 * A write that reconciles remote state before answering takes long enough that an
 * unchanged button reads as a dead click, and the second click sends the request
 * twice. Callers pair `busy` with `disabled` and a label swap.
 *
 *   const [busy, track] = useBusy();
 *   <Button disabled={busy} onClick={() => void track(save)}>
 *     {busy ? 'Saving…' : 'Save'}
 *   </Button>
 */
export function useBusy(): [boolean, <T>(run: () => Promise<T>) => Promise<T>] {
  const [busy, setBusy] = React.useState(false);
  const track = React.useCallback(async <T>(run: () => Promise<T>): Promise<T> => {
    setBusy(true);
    try {
      return await run();
    } finally {
      setBusy(false);
    }
  }, []);
  return [busy, track];
}
